import { useEffect, useState } from "react";
import { api, formatApiError, money } from "../lib/api";
import { toast } from "sonner";
import { ArrowUpRight } from "@phosphor-icons/react";

/**
 * Reusable Cash-Out dialog. Used both from the Till page and from the POS
 * header so staff can record a cash payout without leaving the checkout flow.
 *
 * Props:
 *   onClose() — close without saving
 *   onSaved(record) — successful save (record is the created CashOut doc)
 *   noActiveShift — when true, render an instructional empty-state instead
 *     of the form. The caller decides this from /shifts/active.
 */
export default function CashOutDialog({ onClose, onSaved, noActiveShift = false }) {
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("");
  const [customCat, setCustomCat] = useState("");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (noActiveShift) return;
    api.get("/cash-out/categories").then((r) => {
      const cats = r.data?.categories || [];
      setCategories(cats);
      setCategory(cats[0] || "");
    });
  }, [noActiveShift]);

  const submit = async (e) => {
    e.preventDefault();
    const finalCat = category === "__custom__" ? customCat.trim() : category;
    if (!finalCat) {
      toast.error("Pick a category or type one in");
      return;
    }
    if (!Number(amount) || Number(amount) <= 0) {
      toast.error("Amount must be greater than 0");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/shifts/active/cash-out", {
        category: finalCat,
        amount: Number(amount),
        reason,
      });
      toast.success(`Cash out logged · $${money(amount)}`);
      onSaved?.(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      data-testid="cash-out-dialog"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-white rounded-2xl overflow-hidden"
        style={{ boxShadow: "0 32px 80px rgba(0,0,0,0.18)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-5 border-b border-[#F3F4F6] flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#FEE2E2] text-[#DC2626] flex items-center justify-center shrink-0">
            <ArrowUpRight size={18} weight="bold" />
          </div>
          <div>
            <div className="font-display font-semibold text-[18px]">Cash out</div>
            <div className="text-[12px] text-[#9CA3AF] mt-0.5">Record cash leaving the drawer (deposit, supplier, petty cash, etc).</div>
          </div>
        </div>

        {noActiveShift ? (
          <div className="p-6">
            <div className="rounded-xl bg-[#FEF3C7] border border-[#FCD34D] text-[#92400E] p-4 text-[13px]">
              No shift is open right now. Open a shift on the{" "}
              <a href="/till" className="underline font-semibold">Till</a>{" "}
              page first — cash-outs are tracked against an active shift.
            </div>
            <div className="flex justify-end mt-4">
              <button
                type="button"
                onClick={onClose}
                className="h-11 px-4 rounded-xl border border-[#E5E7EB] text-[14px] font-medium hover:bg-[#F3F4F6]"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4">
            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] uppercase tracking-[0.12em] text-[#4B5563] font-medium">Category</span>
              <select
                data-testid="cash-out-category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="h-11 w-full rounded-xl border border-[#E5E7EB] px-3 text-[14px] focus:border-[#1E5FEB] focus:outline-none bg-white"
              >
                {categories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
                <option value="__custom__">+ Custom (type your own)</option>
              </select>
            </label>
            {category === "__custom__" && (
              <label className="flex flex-col gap-1.5">
                <span className="text-[11px] uppercase tracking-[0.12em] text-[#4B5563] font-medium">Custom category</span>
                <input
                  data-testid="cash-out-category-custom"
                  value={customCat}
                  onChange={(e) => setCustomCat(e.target.value)}
                  placeholder="e.g. Insurance"
                  className="h-11 w-full rounded-xl border border-[#E5E7EB] px-3 text-[14px] focus:border-[#1E5FEB] focus:outline-none"
                />
              </label>
            )}
            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] uppercase tracking-[0.12em] text-[#4B5563] font-medium">Amount ($)</span>
              <input
                data-testid="cash-out-amount"
                type="number"
                step="0.01"
                min="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                autoFocus
                className="h-11 w-full rounded-xl border border-[#E5E7EB] px-3 font-mono text-[15px] focus:border-[#1E5FEB] focus:outline-none"
                required
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-[11px] uppercase tracking-[0.12em] text-[#4B5563] font-medium">Reason / note (optional)</span>
              <input
                data-testid="cash-out-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. To night safe · ANZ deposit"
                className="h-11 w-full rounded-xl border border-[#E5E7EB] px-3 text-[14px] focus:border-[#1E5FEB] focus:outline-none"
              />
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="h-11 px-4 rounded-xl border border-[#E5E7EB] text-[14px] font-medium hover:bg-[#F3F4F6]"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={busy}
                data-testid="cash-out-submit"
                className="h-11 px-5 rounded-xl bg-[#DC2626] text-white text-[14px] font-semibold hover:bg-[#B91C1C] disabled:opacity-60"
              >
                {busy ? "Saving…" : "Record cash out"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
